'use client';

import React, { useState, useCallback, useRef } from 'react';
import {
  Steps,
  Button,
  Input,
  Select,
  Upload,
  message,
  Progress,
  Tag,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  InboxOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  EnvironmentOutlined,
  CameraOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import LocationPicker from './LocationPicker';
import FeatureSelector from './FeatureSelector';
import { useReviewerAuth } from '../contexts/ReviewerAuthContext';
import ReviewerLoginPrompt from '../components/ReviewerLoginPrompt';
import {
  submitCampsiteSubmission,
  getSubmissionUploadUrl,
  registerSubmissionImage,
  savePendingSubmission,
  getPendingSubmission,
  clearPendingSubmission,
} from '../utils/submissionApi';

const { TextArea } = Input;
const { Dragger } = Upload;

// ── Types ─────────────────────────────────────────────────────

interface FormData {
  name: string;
  hosts: string;
  short_desc: string;
  description: string;
  url: string;
  booking_url: string;
  // Address
  street: string;
  street2: string;
  town: string;
  state: string;
  postcode: string;
  phone: string;
  gps_latitude: string;
  gps_longitude: string;
  directions: string;
  // Features & regions
  feature_ids: number[];
  region_ids: number[];
  // Submitter (anonymous)
  submitter_name: string;
  submitter_email: string;
}

interface PhotoItem {
  file: UploadFile;
  caption: string;
  isFeatured: boolean;
}

const INITIAL_FORM: FormData = {
  name: '',
  hosts: '',
  short_desc: '',
  description: '',
  url: '',
  booking_url: '',
  street: '',
  street2: '',
  town: '',
  state: '',
  postcode: '',
  phone: '',
  gps_latitude: '',
  gps_longitude: '',
  directions: '',
  feature_ids: [],
  region_ids: [],
  submitter_name: '',
  submitter_email: '',
};

const CAMPSITE_TYPES = [
  { value: 'Free Camping', label: 'Free Camping' },
  { value: 'Camping', label: 'Camping Site' },
  { value: 'Caravan Parks', label: 'Caravan Park' },
  { value: 'Showgrounds', label: 'Showgrounds' },
];

const AUSTRALIAN_STATES = [
  'New South Wales',
  'Victoria',
  'Queensland',
  'South Australia',
  'Western Australia',
  'Tasmania',
  'Northern Territory',
  'Australian Capital Territory',
];

const MAX_PHOTOS = 10;
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// ── Step definitions ──────────────────────────────────────────

const STEPS = [
  { title: 'Basics', icon: <FileTextOutlined /> },
  { title: 'Location', icon: <EnvironmentOutlined /> },
  { title: 'Features', icon: <AppstoreOutlined /> },
  { title: 'Photos', icon: <CameraOutlined /> },
  { title: 'Submit', icon: <UserOutlined /> },
];

// ── Component ─────────────────────────────────────────────────

const SubmissionForm: React.FC = () => {
  const { isLoggedIn, reviewer: profile } = useReviewerAuth();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const submitAttemptedRef = useRef(false);

  // ── Form helpers ────────────────────────────────────────────

  const set = <K extends keyof FormData>(key: K, value: FormData[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const canNext = (): boolean => {
    if (step === 0) return form.name.trim().length > 0;
    if (step === 1) return form.town.trim().length > 0 || (form.gps_latitude.length > 0 && form.gps_longitude.length > 0);
    return true; // Steps 2-4 are optional
  };

  // ── Photo handling ──────────────────────────────────────────

  const beforeUpload = useCallback(
    (file: RcFile): boolean | string => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        message.error(`${file.name} is not supported (JPG, PNG, WEBP only).`);
        return Upload.LIST_IGNORE;
      }
      if (file.size > MAX_SIZE_BYTES) {
        message.error(`${file.name} exceeds ${MAX_SIZE_MB}MB limit.`);
        return Upload.LIST_IGNORE;
      }
      if (photos.length >= MAX_PHOTOS) {
        message.warning(`Maximum ${MAX_PHOTOS} photos allowed.`);
        return Upload.LIST_IGNORE;
      }

      // Add to our managed list
      setPhotos((prev) => [
        ...prev,
        {
          file: file as unknown as UploadFile,
          caption: '',
          isFeatured: prev.length === 0, // First photo auto-featured
        },
      ]);
      return false; // Prevent auto upload
    },
    [photos.length],
  );

  const removePhoto = (index: number) => {
    setPhotos((prev) => {
      const next = prev.filter((_, i) => i !== index);
      // If removed photo was featured, make first one featured
      if (prev[index]?.isFeatured && next.length > 0) {
        next[0].isFeatured = true;
      }
      return next;
    });
  };

  const setFeatured = (index: number) => {
    setPhotos((prev) =>
      prev.map((p, i) => ({ ...p, isFeatured: i === index })),
    );
  };

  const setCaption = (index: number, caption: string) => {
    setPhotos((prev) =>
      prev.map((p, i) => (i === index ? { ...p, caption } : p)),
    );
  };

  // ── Submission ──────────────────────────────────────────────

  const doSubmit = async () => {
    setSubmitting(true);
    setUploadProgress(0);

    try {
      // 1. Create the submission
      const payload: Record<string, unknown> = {
        name: form.name,
        description: form.description,
        short_desc: form.short_desc,
        url: form.url,
        booking_url: form.booking_url,
        hosts: form.hosts,
        address: {
          street: form.street,
          street2: form.street2,
          town: form.town,
          state: form.state,
          postcode: form.postcode,
          phone: form.phone,
          gps_latitude: form.gps_latitude,
          gps_longitude: form.gps_longitude,
          directions: form.directions,
        },
        features: form.feature_ids,
        regions: form.region_ids,
      };

      if (!isLoggedIn) {
        payload.submitter_name = form.submitter_name;
        payload.submitter_email = form.submitter_email;
      }

      const submission = await submitCampsiteSubmission(payload);
      const submissionId = submission.id;
      setUploadProgress(20);

      // 2. Upload photos
      if (photos.length > 0) {
        const photoStep = 70 / photos.length;
        for (let i = 0; i < photos.length; i++) {
          const photo = photos[i];
          const file = photo.file as unknown as File;

          // Get presigned URL
          const { upload_url, image_src } = await getSubmissionUploadUrl(
            submissionId,
            file.name,
            file.type,
          );

          // Upload to S3
          await fetch(upload_url, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });

          // Register the image
          await registerSubmissionImage(submissionId, {
            image_src,
            caption: photo.caption,
            content_type: file.type,
            file_size: file.size,
            sort_order: i,
            is_featured: photo.isFeatured,
          });

          setUploadProgress(20 + Math.round((i + 1) * photoStep));
        }
      }

      setUploadProgress(100);
      clearPendingSubmission();
      setSubmitted(true);
      message.success('Campsite submitted for review!');
    } catch (err) {
      console.error('Submission error:', err);
      message.error(
        err instanceof Error ? err.message : 'Submission failed. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Validate submitter info
    if (!isLoggedIn) {
      if (!form.submitter_name.trim() || !form.submitter_email.trim()) {
        message.warning('Please enter your name and email, or sign in.');
        return;
      }
    }

    // Progressive auth: if not logged in, offer login
    if (!isLoggedIn && !submitAttemptedRef.current) {
      submitAttemptedRef.current = true;
      // Save form data to sessionStorage for resumption after OAuth
      savePendingSubmission({ form, photos: photos.map(p => ({ caption: p.caption, isFeatured: p.isFeatured })) });
      setShowLoginPrompt(true);
      return;
    }

    await doSubmit();
  };

  const handleLoginSuccess = () => {
    setShowLoginPrompt(false);
    doSubmit();
  };

  const handleSkipLogin = () => {
    setShowLoginPrompt(false);
    doSubmit();
  };

  // ── Success state ───────────────────────────────────────────

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mb-6">
          <CheckOutlined style={{ fontSize: 36, color: '#22c55e' }} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Submission Received!
        </h2>
        <p className="text-gray-500 max-w-md mb-6">
          Thank you for contributing to Camping Australia! Our team will review
          your campsite listing and publish it once approved. We&apos;ll notify you
          via email.
        </p>
        <Button
          type="primary"
          size="large"
          onClick={() => {
            setSubmitted(false);
            setStep(0);
            setForm(INITIAL_FORM);
            setPhotos([]);
            submitAttemptedRef.current = false;
          }}
          className="!rounded-lg"
        >
          Submit Another Campsite
        </Button>
      </div>
    );
  }

  // ── Step renderers ──────────────────────────────────────────

  const renderBasics = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Campsite Name <span className="text-red-500">*</span>
        </label>
        <Input
          size="large"
          placeholder="e.g., Timbertown Campground"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          maxLength={200}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Campsite Type
        </label>
        <Select
          size="large"
          placeholder="Select type..."
          value={form.hosts || undefined}
          onChange={(v) => set('hosts', v)}
          options={CAMPSITE_TYPES}
          className="w-full"
          allowClear
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Short Description
        </label>
        <Input
          placeholder="A brief 1-2 sentence summary"
          value={form.short_desc}
          onChange={(e) => set('short_desc', e.target.value)}
          maxLength={300}
          showCount
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Full Description
        </label>
        <TextArea
          rows={5}
          placeholder="Describe the campsite in detail — facilities, atmosphere, surroundings, tips for visitors..."
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          maxLength={5000}
          showCount
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Website
          </label>
          <Input
            placeholder="https://example.com"
            value={form.url}
            onChange={(e) => set('url', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Booking URL
          </label>
          <Input
            placeholder="https://booking.example.com"
            value={form.booking_url}
            onChange={(e) => set('booking_url', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderLocation = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Pin Location on Map
        </label>
        <LocationPicker
          latitude={form.gps_latitude}
          longitude={form.gps_longitude}
          onChange={(coords) => {
            set('gps_latitude', coords.latitude);
            set('gps_longitude', coords.longitude);
          }}
          onAddressFound={(addr) => {
            if (addr.street && !form.street) set('street', addr.street);
            if (addr.town && !form.town) set('town', addr.town);
            if (addr.state && !form.state) set('state', addr.state);
            if (addr.postcode && !form.postcode) set('postcode', addr.postcode);
          }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street Address
          </label>
          <Input
            placeholder="123 Forest Road"
            value={form.street}
            onChange={(e) => set('street', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Town / Suburb <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Wauchope"
            value={form.town}
            onChange={(e) => set('town', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State
          </label>
          <Select
            placeholder="Select state"
            value={form.state || undefined}
            onChange={(v) => set('state', v)}
            options={AUSTRALIAN_STATES.map((s) => ({ value: s, label: s }))}
            className="w-full"
            allowClear
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Postcode
          </label>
          <Input
            placeholder="2446"
            value={form.postcode}
            onChange={(e) => set('postcode', e.target.value)}
            maxLength={4}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <Input
            placeholder="02 1234 5678"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Directions / Access Notes
        </label>
        <TextArea
          rows={3}
          placeholder="How to get there — e.g., Turn off Pacific Highway at..."
          value={form.directions}
          onChange={(e) => set('directions', e.target.value)}
          maxLength={2000}
        />
      </div>
    </div>
  );

  const renderFeatures = () => (
    <div>
      <p className="text-sm text-gray-500 mb-4">
        Select the amenities and features available at this campsite. This helps
        other campers find sites that match their needs.
      </p>
      <FeatureSelector
        selectedIds={form.feature_ids}
        onChange={(ids) => set('feature_ids', ids)}
      />
    </div>
  );

  const renderPhotos = () => (
    <div className="space-y-5">
      <Dragger
        multiple
        beforeUpload={beforeUpload}
        showUploadList={false}
        accept={ACCEPTED_TYPES.join(',')}
        disabled={photos.length >= MAX_PHOTOS}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag photos to upload
        </p>
        <p className="ant-upload-hint">
          JPG, PNG, WEBP up to {MAX_SIZE_MB}MB each. Max {MAX_PHOTOS} photos.
        </p>
      </Dragger>

      {photos.length > 0 && (
        <div className="space-y-3">
          {photos.map((photo, i) => (
            <div
              key={photo.file.uid || i}
              className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={URL.createObjectURL(photo.file as unknown as File)}
                alt={photo.caption || `Photo ${i + 1}`}
                className="w-20 h-20 object-cover rounded-lg shrink-0"
              />
              <div className="flex-1 min-w-0 space-y-2">
                <Input
                  size="small"
                  placeholder="Add a caption..."
                  value={photo.caption}
                  onChange={(e) => setCaption(i, e.target.value)}
                  maxLength={200}
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFeatured(i)}
                    className={`text-xs flex items-center gap-1 transition ${
                      photo.isFeatured
                        ? 'text-amber-500 font-medium'
                        : 'text-gray-400 hover:text-amber-400'
                    }`}
                  >
                    {photo.isFeatured ? (
                      <StarFilled className="text-sm" />
                    ) : (
                      <StarOutlined className="text-sm" />
                    )}
                    {photo.isFeatured ? 'Featured' : 'Set as featured'}
                  </button>
                  <Tag className="!text-[10px] !m-0">
                    {((photo.file as unknown as File).size / 1024 / 1024).toFixed(1)}MB
                  </Tag>
                </div>
              </div>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => removePhoto(i)}
                className="!text-gray-400 hover:!text-red-500 shrink-0"
                size="small"
              />
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-400">
        {photos.length} of {MAX_PHOTOS} photos added.
        The featured photo will be the main listing image.
      </p>
    </div>
  );

  const renderSubmit = () => (
    <div className="space-y-6">
      {/* Summary */}
      <div className="rounded-xl border border-gray-200 p-5 space-y-3">
        <h3 className="font-semibold text-gray-800">Submission Summary</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <span className="text-gray-500">Name:</span>{' '}
            <span className="font-medium">{form.name}</span>
          </div>
          <div>
            <span className="text-gray-500">Type:</span>{' '}
            <span className="font-medium">{form.hosts || 'Not specified'}</span>
          </div>
          <div>
            <span className="text-gray-500">Location:</span>{' '}
            <span className="font-medium">
              {[form.town, form.state].filter(Boolean).join(', ') || 'Not specified'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">GPS:</span>{' '}
            <span className="font-medium">
              {form.gps_latitude && form.gps_longitude
                ? `${form.gps_latitude}, ${form.gps_longitude}`
                : 'Not set'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Features:</span>{' '}
            <span className="font-medium">{form.feature_ids.length} selected</span>
          </div>
          <div>
            <span className="text-gray-500">Photos:</span>{' '}
            <span className="font-medium">{photos.length} uploaded</span>
          </div>
        </div>
      </div>

      {/* Submitter details */}
      {isLoggedIn && profile ? (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 flex items-center gap-3">
          {profile.profile_image && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={profile.profile_image}
              alt={profile.display_name}
              className="w-10 h-10 rounded-full"
            />
          )}
          <div>
            <p className="text-sm font-medium text-green-800">
              Submitting as {profile.display_name}
            </p>
            <p className="text-xs text-green-600">{profile.email}</p>
          </div>
          <CheckOutlined className="ml-auto text-green-500" />
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Enter your details so we can notify you when your submission is
            reviewed, or{' '}
            <button
              type="button"
              onClick={() => setShowLoginPrompt(true)}
              className="text-sky-600 font-medium hover:underline"
            >
              sign in
            </button>{' '}
            to link it to your account.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Name <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="Jane Smith"
                value={form.submitter_name}
                onChange={(e) => set('submitter_name', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Email <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={form.submitter_email}
                onChange={(e) => set('submitter_email', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Upload progress */}
      {submitting && (
        <div className="space-y-2">
          <Progress percent={uploadProgress} status="active" />
          <p className="text-xs text-gray-400 text-center">
            {uploadProgress < 20
              ? 'Creating submission...'
              : uploadProgress < 90
                ? 'Uploading photos...'
                : 'Finalising...'}
          </p>
        </div>
      )}
    </div>
  );

  const stepContent = [
    renderBasics,
    renderLocation,
    renderFeatures,
    renderPhotos,
    renderSubmit,
  ];

  // ── Main render ─────────────────────────────────────────────

  return (
    <div className="max-w-3xl mx-auto">
      <Steps
        current={step}
        items={STEPS}
        className="mb-8"
        responsive
        size="small"
      />

      <div className="min-h-[400px]">{stepContent[step]()}</div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => setStep((s) => s - 1)}
          disabled={step === 0}
          className="!rounded-lg"
        >
          Back
        </Button>

        <span className="text-xs text-gray-400">
          Step {step + 1} of {STEPS.length}
        </span>

        {step < STEPS.length - 1 ? (
          <Button
            type="primary"
            onClick={() => setStep((s) => s + 1)}
            disabled={!canNext()}
            className="!rounded-lg"
          >
            Next <ArrowRightOutlined />
          </Button>
        ) : (
          <Button
            type="primary"
            size="large"
            icon={<CheckOutlined />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
            className="!rounded-lg !bg-gradient-to-r !from-sky-500 !to-indigo-500 !border-none hover:!brightness-110"
          >
            Submit for Review
          </Button>
        )}
      </div>

      {/* Login prompt overlay */}
      {showLoginPrompt && (
        <ReviewerLoginPrompt
          isOpen={showLoginPrompt}
          onClose={() => setShowLoginPrompt(false)}
          onLoginSuccess={handleLoginSuccess}
          title="Sign in to submit"
          description="Sign in to link this submission to your account, or continue as a guest."
          showSkip
          onSkip={handleSkipLogin}
        />
      )}
    </div>
  );
};

export default SubmissionForm;
